import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useAddPresetGroup,
  useGetAllPresets,
  useUpdatePresetGroup,
} from '@restDataAccess/camera/cameraControlApi.dataHooks.ts';
import { Button, FlexAligner, ModalContent, ModalFooter, ModalHeader } from '@scaleaq/scaleui';
import { AddOrUpdatePresetGroupRequest, PresetGroupInfo } from '@scaleaq/sitecontrol-lib';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ControlledCreatableMultiSelect,
  ControlledTextOrNumberField,
  FeedbackAlert,
  FeedbackAlertProps,
} from '@/components';
import { StyledModal } from '@/styles.ts';
import { ModalControllerType } from '@/types.ts';
import { Icon, responsive } from '@/utils';

import { presetModalTokens } from './presetModal.tokens.ts';

export const AddOrEditPresetGroupModal = ({
  modalController,
  onDefineNew,
  onSaved,
  selectedPresetGroup,
}: {
  modalController: ModalControllerType;
  onDefineNew: () => void;
  onSaved: (saved: { name: string; operation: 'added' | 'updated' }) => void;
  selectedPresetGroup?: PresetGroupInfo;
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['cameraControl', 'common']);
  const { data: presets, isPending: isPresetsPending } = useGetAllPresets();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<AddOrUpdatePresetGroupRequest>({
    defaultValues: {
      name: '',
      presetIds: [],
    },
    mode: 'all',
  });
  const [feedback, setFeedback] = useState<FeedbackAlertProps>();
  const { mutate: addMutate, isPending: isAdding } = useAddPresetGroup();
  const { mutate: updateMutate, isPending: isUpdating } = useUpdatePresetGroup({
    presetGroupId: selectedPresetGroup?.id ?? '',
  });
  const titleKey = selectedPresetGroup ? 'preset.editGroup' : 'preset.defineNewGroup';

  useEffect(() => {
    if (!modalController.open) {
      return;
    }

    setFeedback(undefined);
    if (!selectedPresetGroup) {
      reset();
      return;
    }

    reset(
      {
        name: selectedPresetGroup.name,
        presetIds: selectedPresetGroup.presets?.map(preset => preset.id) ?? [],
      },
      { keepDefaultValues: true }
    );
  }, [modalController.open, reset, selectedPresetGroup]);

  const presetOptions = useMemo(
    () =>
      (presets ?? [])
        .map(preset => ({ label: preset.name, value: preset.id }))
        .sort((left, right) => left.label.localeCompare(right.label, language)),
    [language, presets]
  );

  const onSaveHandler = useCallback(
    (data: AddOrUpdatePresetGroupRequest) => {
      const mutate = selectedPresetGroup ? updateMutate : addMutate;
      mutate(data, {
        onSuccess: () => {
          modalController.onClose();
          onSaved({
            name: data.name,
            operation: selectedPresetGroup ? 'updated' : 'added',
          });
        },
        onError: error => {
          const message = error instanceof Error ? error.message : '';
          setFeedback({
            text: t(
              selectedPresetGroup ? 'common:snackbar.error.update' : 'common:snackbar.error.add',
              { name: data.name, message }
            ),
            variant: 'alarm',
          });
        },
      });
    },
    [addMutate, modalController, onSaved, selectedPresetGroup, t, updateMutate]
  );

  return (
    <>
      <Button
        label={t('preset.defineNewGroup')}
        headIcon={Icon.CirclePlus}
        variant={'ghost'}
        onClick={onDefineNew}
      />
      <StyledModal
        closeOnBackdropClick={true}
        closeOnEscapeClick={true}
        open={modalController.open}
        onClose={modalController.onClose}
        aria-label={t(titleKey)}
        $width={presetModalTokens.addOrEdit.width}
        $maxWidth={presetModalTokens.addOrEdit.maxWidth}
        $minHeight={presetModalTokens.addOrEdit.minHeight}
      >
        <ModalHeader title={t(titleKey)} onClose={modalController.onClose} />
        <ModalContent>
          <FeedbackAlert {...feedback} loading={isAdding || isUpdating} />
          <form onSubmit={handleSubmit(onSaveHandler)}>
            <FlexAligner direction='column' gap={responsive.spacing.xlarge}>
              <ControlledTextOrNumberField
                control={control}
                name={'name'}
                label={t('preset.groupName')}
                rules={{ required: t('common:nameRequired') }}
                critical={errors.name?.message}
              />
              <ControlledCreatableMultiSelect
                key={`${modalController.open}-${selectedPresetGroup?.id ?? 'new'}`}
                allowCreate={false}
                control={control}
                isLoading={isPresetsPending}
                label={t('preset.presetsInGroup')}
                name='presetIds'
                options={presetOptions}
                placeholder={t('preset.presetsInGroupPlaceholder')}
              />
            </FlexAligner>
          </form>
        </ModalContent>
        <ModalFooter
          actionButtons={[
            {
              label: t('common:save'),
              onClick: handleSubmit(onSaveHandler),
            },
            { label: t('common:close'), onClick: modalController.onClose },
          ]}
        />
      </StyledModal>
    </>
  );
};
